// import React, { useState } from "react";
// import MainOverview from "./MainOverview/MainOverview";
// import Waveheight from "./Waveheight/Waveheight";
// import SwellWaveheight from "./SwellWaveheight/SwellWaveheight";
// import WindSpeed from "./WindSpeed/WindSpeed";
// import "./OceanState.css";

// const Oceansate = () => {
//   const [activeTab, setActiveTab] = useState("mainview");

//   return (
//     <div className="oceansate-container">
//       <div className="tab-buttons">
//         <button
//           onClick={() => setActiveTab("mainview")}
//           className={`tab-button ${activeTab === "mainview" ? "active" : ""}`}
//         >
//           Overview
//         </button>
//         <button
//           onClick={() => setActiveTab("wave")}
//           className={`tab-button ${activeTab === "wave" ? "active" : ""}`}
//         >
//           Wave
//         </button>
//         <button
//           onClick={() => setActiveTab("swell")}
//           className={`tab-button ${activeTab === "swell" ? "active" : ""}`}
//         >
//           Swell
//         </button>
//         <button
//           onClick={() => setActiveTab("wind")}
//           className={`tab-button ${activeTab === "wind" ? "active" : ""}`}
//         >
//           Wind
//         </button>
//       </div>

//       <div className="tab-content">
//         {activeTab === "mainview" && <MainOverview />}
//         {activeTab === "wave" && <Waveheight />}
//         {activeTab === "swell" && <SwellWaveheight />}
//         {activeTab === "wind" && <WindSpeed />}
//       </div>
//     </div>
//   );
// };

// export default Oceansate;

import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  CircleMarker,
  Popup,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaCircleInfo } from "react-icons/fa6"; // Keep info button
import { BsChevronDown, BsChevronUp } from "react-icons/bs"; // For dropdown arrow
import { IoClose } from "react-icons/io5"; // For close button
import { MdLabelOutline, MdLabelOff } from "react-icons/md"; // For label toggle
import axios from "axios";
import moment from "moment";
import Chart from "chart.js/auto";
import { MapHomeControl } from "../../../common/MapControls/MapControls";

// Leaflet's own drag/click handling swallows clicks on overlay controls
// unless propagation to the map is explicitly disabled on the DOM node.
const useNoMapClick = () =>
  useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

// Utility function (simulated)
const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

// Simplified labels for legend display (removed <br> for cleaner inline display)
const WAVE_HEIGHT_CATEGORIES = [
  { label: "No Risk (< 1 m)", max: 1, color: "rgb(148, 203, 85)" },
  { label: "Low (≥ 1 to ≤ 1.5 m)", max: 1.5, color: "rgb(243, 235, 18)" },
  { label: "Medium (> 1.5 to ≤ 2.0 m)", max: 2.0, color: "rgb(243, 155, 58)" },
  { label: "High (> 2 m)", max: Infinity, color: "rgb(230, 45, 58)" },
];

const SWELL_HEIGHT_CATEGORIES = [
  { label: "No Risk (< 0.0 to ≤ 0.1 m)", max: 1, color: "rgb(148, 203, 85)" },
  { label: "Low (≥ 1.0 to ≤ 1.5 m)", max: 1.5, color: "rgb(243, 235, 18)" },
  { label: "Medium (≥ 1.5 to ≤ 2.0 m)", max: 2.0, color: "rgb(243, 155, 58)" },
  { label: "High (> 2.0 m)", max: Infinity, color: "rgb(230, 45, 58)" },
];

const WIND_SPEED_CATEGORIES = [
  { label: "No Risk (< 15 km/hr)", max: 15, color: "rgb(148, 203, 85)" },
  { label: "Low (≥ 15 to ≤ 20 km/hr)", max: 20, color: "rgb(243, 155, 58)" },
  { label: "Medium (> 20 to ≤ 25 km/hr)", max: 25, color: "rgb(243, 155, 58)" },
  { label: "High (> 25 km/hr)", max: Infinity, color: "rgb(230, 45, 58)" },
];

const InfoButton = ({ onClick }) => {
  const noClickRef = useNoMapClick();
  return (
    <div
      ref={noClickRef}
      style={{
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const WaveChart = ({ data, locationName }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    const chartCanvas = chartRef.current?.getContext("2d");

    if (chartCanvas) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const chartData = {
        labels: data.map((d) => d.time),
        datasets: [
          {
            label: "Wave Height (m)",
            data: data.map((d) => d.waveHeight),
            borderColor: "#42a5f5",
            backgroundColor: "rgba(66, 165, 245, 0.08)",
            borderWidth: 2.5,
            fill: true,
            yAxisID: "y1",
            pointBackgroundColor: "#42a5f5",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
          },
          {
            label: "Swell Height (m)",
            data: data.map((d) => d.swellWaveHeight),
            borderColor: "#ffa726",
            backgroundColor: "rgba(255, 167, 38, 0.08)",
            borderWidth: 2.5,
            fill: true,
            yAxisID: "y1",
            pointBackgroundColor: "#ffa726",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
          },
          {
            label: "Wind Speed (km/h)",
            data: data.map((d) => d.windSpeed),
            borderColor: "#66bb6a",
            backgroundColor: "rgba(102, 187, 106, 0.08)",
            borderWidth: 2.5,
            fill: true,
            yAxisID: "y2",
            pointBackgroundColor: "#66bb6a",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
          },
        ],
      };

      try {
        const newChart = new Chart(chartCanvas, {
          type: "line",
          data: chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "index",
              intersect: false,
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: "Forecast Time",
                  font: { size: 11, weight: "600" },
                  color: "#555",
                },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: 12,
                  font: { size: 9 },
                  color: "#777",
                  maxRotation: 45,
                  callback: function (value, index, values) {
                    const fullTimeStr = this.getLabelForValue(value);
                    const momentObj = moment(fullTimeStr);

                    if (momentObj.isValid()) {
                      const formattedHour = momentObj.format("HH");
                      if (formattedHour === "00") {
                        return momentObj.format("MMM DD");
                      } else {
                        return formattedHour + ":00";
                      }
                    }
                    return fullTimeStr;
                  },
                },
                grid: {
                  color: "rgba(0,0,0,0.04)",
                  drawBorder: false,
                },
              },
              y1: {
                type: "linear",
                position: "left",
                title: {
                  display: true,
                  text: "Height (m)",
                  font: { size: 11, weight: "600" },
                  color: "#555",
                },
                ticks: {
                  font: { size: 9 },
                  color: "#777",
                  padding: 4,
                },
                min: 0,
                grid: {
                  color: "rgba(0,0,0,0.06)",
                  drawBorder: false,
                },
              },
              y2: {
                type: "linear",
                position: "right",
                title: {
                  display: true,
                  text: "Wind (km/h)",
                  font: { size: 11, weight: "600" },
                  color: "#555",
                },
                ticks: {
                  font: { size: 9 },
                  color: "#777",
                  padding: 4,
                },
                min: 0,
                grid: {
                  drawOnChartArea: false,
                  drawBorder: false,
                },
              },
            },
            plugins: {
              title: {
                display: false,
              },
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  font: { size: 10, weight: "500" },
                  color: "#555",
                  usePointStyle: true,
                  pointStyle: "circle",
                  padding: 12,
                  boxWidth: 8,
                },
              },
              tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: "rgba(30, 30, 30, 0.9)",
                titleFont: { size: 11, weight: "600" },
                bodyFont: { size: 10 },
                padding: 10,
                cornerRadius: 8,
                boxPadding: 4,
                callbacks: {
                  label: (context) => {
                    let label = context.dataset.label || "";
                    if (label) label += ": ";
                    if (context.parsed.y !== null) {
                      label += context.parsed.y.toFixed(2);
                    }
                    return label;
                  },
                  title: (context) => {
                    if (context && context[0] && context[0].label) {
                      return moment(context[0].label).format("MMM DD, HH:mm");
                    }
                    return null;
                  },
                },
              },
            },
            elements: {
              line: {
                tension: 0.35,
              },
              point: {
                radius: 2,
                hoverRadius: 5,
                hitRadius: 10,
              },
            },
          },
        });
        chartInstanceRef.current = newChart;
        chartRef.current.chart = newChart;
      } catch (error) {
        console.error("Error creating chart:", error);
      }
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data, locationName]);

  return <canvas ref={chartRef} width={480} height={280} />;
};

// Component for a single legend section within the collapsible box
const LegendSection = ({ title, categories }) => (
  <div style={{ marginBottom: "14px" }}>
    <div style={{
      fontWeight: "700",
      marginBottom: "8px",
      fontSize: "11px",
      color: "#333333",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
    }}>{title}</div>
    {categories.map((category) => (
      <div
        key={category.label}
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "5px",
          gap: "8px",
        }}
      >
        <span
          style={{
            width: "14px",
            height: "14px",
            backgroundColor: category.color,
            borderRadius: "50%",
            border: "1.5px solid rgba(0, 0, 0, 0.15)",
            flexShrink: 0,
            boxShadow: `0 0 6px ${category.color}80`,
          }}
        />
        <span style={{ fontSize: "11px", lineHeight: "1.3", color: "#333333", fontWeight: "500" }}>{category.label}</span>
      </div>
    ))}
  </div>
);


const Oceansate = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [oceanData, setOceanData] = useState([]);
  const [windData, setWindData] = useState([]);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [, setError] = useState(null);
  const [isLegendOpen, setIsLegendOpen] = useState(false); // State for legend visibility
  const [showLabels, setShowLabels] = useState(false); // State for label visibility
  const legendRef = useRef(null);
  const legendBoxRef = useCallback((node) => {
    legendRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!isLegendOpen) return;
    const handleClickOutside = (e) => {
      if (legendRef.current && !legendRef.current.contains(e.target)) setIsLegendOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLegendOpen]);
  const controlsRef = useNoMapClick();

  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((response) => response.json())
      .then((data) => setBoundaryData(data))
      .catch((error) => console.error("Error loading boundary data:", error));
  }, []);

  useEffect(() => {
    const fetchOceanData = async (sourceId, setData) => {
      try {
        const response = await axios.get(
          `https://tl-osfas.rimes.int/api/weatherforecasts?sourceId=${sourceId}`
        );

        if (Array.isArray(response.data.data)) {
          const filteredData = response.data.data.filter(
            (item) => item.parameter.id === 3 || item.parameter.id === 5
          );
          setData(filteredData);
        } else {
          const errorMessage = `API response.data (Source ${sourceId}) is not an array:`;
          console.error(errorMessage, response.data);
          setError(errorMessage);
          setData([]);
        }
      } catch (error) {
        const errorMessage = `Error fetching ocean data (Source ${sourceId}):`;
        console.error(errorMessage, error);
        setError(errorMessage);
        setData([]);
      }
    };

    const fetchWindData = async (sourceId, setData) => {
      try {
        const response = await axios.get(
          `https://tl-osfas.rimes.int/api/weatherforecasts?sourceId=${sourceId}`
        );
        if (Array.isArray(response.data.data)) {
          const filteredData = response.data.data.filter(
            (item) => item.parameter.id === 7
          );
          setData(filteredData);
        } else {
          const errorMessage = `API response.data (Source ${sourceId}) is not an array:`;
          console.error(errorMessage, response.data);
          setError(errorMessage);
          setData([]);
        }
      } catch (error) {
        const errorMessage = `Error fetching wind data (Source ${sourceId}):`;
        console.error(errorMessage, error);
        setError(errorMessage);
        setData([]);
      }
    };

    fetchOceanData(1, setOceanData);
    fetchWindData(4, setWindData);
  }, []);

  const groupedOceanData = oceanData.reduce((acc, item) => {
    const locationName = item.location.name;
    if (!acc[locationName]) {
      acc[locationName] = [];
    }
    const validTimeMoment = item.valid_time && moment(item.valid_time);
    const formattedTime =
      validTimeMoment && validTimeMoment.isValid()
        ? validTimeMoment.format("YYYY-MM-DD HH:mm")
        : "Invalid Time";

    if (item.parameter.id === 3) {
      // Wave Height
      const existing = acc[locationName].find((d) => d.time === formattedTime);
      if (existing) {
        existing.waveHeight = item.value;
      } else {
        acc[locationName].push({
          time: formattedTime,
          waveHeight: item.value,
          swellWaveHeight: 0,
          windSpeed: 0,
        });
      }
    } else if (item.parameter.id === 5) {
      // Swell Wave Height
      const existing = acc[locationName].find((d) => d.time === formattedTime);
      if (existing) {
        existing.swellWaveHeight = item.value;
      } else {
        acc[locationName].push({
          time: formattedTime,
          waveHeight: 0,
          swellWaveHeight: item.value,
          windSpeed: 0,
        });
      }
    }
    return acc;
  }, {});

  const groupedWindData = windData.reduce((acc, item) => {
    const locationName = item.location.name;
    if (!acc[locationName]) {
      acc[locationName] = [];
    }
    const validTimeMoment = item.valid_time && moment(item.valid_time);
    const formattedTime =
      validTimeMoment && validTimeMoment.isValid()
        ? validTimeMoment.format("YYYY-MM-DD HH:mm")
        : "Invalid Time";

    acc[locationName].push({
      time: formattedTime,
      windSpeed: item.value,
    });
    return acc;
  }, {});

  for (const locationName in groupedOceanData) {
    const oceanDataArray = groupedOceanData[locationName];
    const windDataArray = groupedWindData[locationName] || [];

    oceanDataArray.forEach((oceanItem) => {
      const matchingWindItem = windDataArray.find(
        (windItem) => windItem.time === oceanItem.time
      );
      if (matchingWindItem) {
        oceanItem.windSpeed = matchingWindItem.windSpeed;
      }
    });
  }

  const getWaveCategory = (waveHeight) => {
    return (
      WAVE_HEIGHT_CATEGORIES.find((category) => waveHeight <= category.max) || {
        color: "gray",
        label: "No Data",
      }
    );
  };

  // Compute analytics: per-location max wave height & category counts
  const analytics = useMemo(() => {
    const uniqueLocations = oceanData.reduce((acc, item) => {
      if (!acc.some((loc) => loc.id === item.location.id)) {
        acc.push(item.location);
      }
      return acc;
    }, []);

    let noRisk = 0, low = 0, medium = 0, high = 0;

    uniqueLocations.forEach((location) => {
      const combinedData = groupedOceanData[location.name] || [];
      let maxWH = 0;
      combinedData.forEach((d) => {
        if (d.waveHeight > maxWH) maxWH = d.waveHeight;
      });
      const cat = getWaveCategory(maxWH);
      const catLabel = cat.label.split("(")[0].trim();
      if (catLabel === "No Risk") noRisk++;
      else if (catLabel === "Low") low++;
      else if (catLabel === "Medium") medium++;
      else if (catLabel === "High") high++;
    });

    // Compute forecast period from valid_time
    let minTime = null, maxTime = null;
    oceanData.forEach((item) => {
      if (item.valid_time) {
        const t = moment(item.valid_time);
        if (t.isValid()) {
          if (!minTime || t.isBefore(minTime)) minTime = t;
          if (!maxTime || t.isAfter(maxTime)) maxTime = t;
        }
      }
    });

    return {
      total: uniqueLocations.length,
      noRisk,
      low,
      medium,
      high,
      periodStart: minTime ? minTime.format("DD MMM YYYY") : null,
      periodEnd: maxTime ? maxTime.format("DD MMM YYYY") : null,
    };
  }, [oceanData, groupedOceanData]);

  return (
    <div className="wind-speed-cyclone-container">
      <style>{`
        .ocean-permanent-tooltip {
          background: rgba(255, 255, 255, 0.95) !important;
          border: 1px solid #1976d2 !important;
          border-radius: 6px !important;
          padding: 6px 10px !important;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2) !important;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
        }
        .ocean-permanent-tooltip::before {
          border-top-color: #1976d2 !important;
        }
      `}</style>

      {/* Analytics Bar */}
      {oceanData.length > 0 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          marginBottom: "8px",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}>
            <span style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#1976d2",
            }}>
              Total: {analytics.total} Forecast Points
            </span>
            {analytics.periodStart && analytics.periodEnd && (
              <span style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#666",
                backgroundColor: "#f5f7fa",
                padding: "4px 12px",
                borderRadius: "4px",
              }}>
                Period: {analytics.periodStart} — {analytics.periodEnd}
              </span>
            )}
          </div>

          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            alignItems: "center",
          }}>
            {[
              { label: "No Risk", count: analytics.noRisk, color: "rgb(148, 203, 85)" },
              { label: "Low", count: analytics.low, color: "rgb(243, 235, 18)" },
              { label: "Medium", count: analytics.medium, color: "rgb(243, 155, 58)" },
              { label: "High", count: analytics.high, color: "rgb(230, 45, 58)" },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "5px 12px",
                  backgroundColor: "#f1f2f5",
                  borderRadius: "6px",
                  border: "1px solid #f1f2f5",
                  whiteSpace: "nowrap",
                  fontSize: "13px",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: item.color,
                    borderRadius: "3px",
                    flexShrink: 0,
                    boxShadow: `0 2px 4px ${item.color}40`,
                  }}
                />
                <span style={{ color: "#666", fontWeight: "600" }}>{item.label}</span>
                <span style={{ color: item.color === "rgb(243, 235, 18)" ? "#c8a415" : item.color, fontWeight: "700" }}>
                  ({item.count})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mapocean">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          style={{ width: "100%", height: "600px",borderRadius: "10px" }}
        >
          <MapHomeControl
            center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
            zoom={INITIAL_VIEW_STATE.zoom}
          />
          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              Three-day wave, swell and wind speed forecasts for the ocean
              stations from data source are available here.
            </div>
          )}

          {/* Bottom-left controls: Info, Show Labels, Legend — inline */}
          <div
            ref={controlsRef}
            style={{
              position: "absolute",
              bottom: "20px",
              left: "12px",
              zIndex: 1000,
              display: "flex",
              alignItems: "flex-end",
              gap: "8px",
            }}
          >
            <InfoButton onClick={() => setInfo(!info)} />

            <button
              onClick={() => setShowLabels(prev => !prev)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                color: "#333333",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "600",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                transition: "all 0.2s ease",
                letterSpacing: "0.3px",
              }}
              title={showLabels ? "Hide all labels" : "Show all labels"}
            >
              {showLabels ? <MdLabelOff size={16} /> : <MdLabelOutline size={16} />}
              {showLabels ? "HIDE FORECAST POINTS" : "SHOW FORECAST POINTS"}
            </button>
          </div>

          {/* Combined Collapsible Legend Box — bottom right */}
          <div
            ref={legendBoxRef}
            style={{
              position: "absolute",
              bottom: "20px",
              right: "12px",
              zIndex: 1000,
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(12px) brightness(1.1)",
                WebkitBackdropFilter: "blur(12px) brightness(1.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                width: "260px",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                overflow: "hidden",
                transition: "all 0.3s ease",
              }}
            >
              {/* Legend Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 14px",
                  borderBottom: isLegendOpen ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => setIsLegendOpen(!isLegendOpen)}
              >
                <div style={{ fontWeight: "700", fontSize: "12px", color: "#333333", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                  Legend
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  {isLegendOpen ? (
                    <BsChevronUp size={16} color="#333333" />
                  ) : (
                    <BsChevronDown size={16} color="#333333" />
                  )}
                  {isLegendOpen && (
                    <IoClose size={16} color="#333333" onClick={(e) => { e.stopPropagation(); setIsLegendOpen(false); }} style={{ cursor: 'pointer' }}/>
                  )}
                </div>
              </div>

              {/* Legend Content (conditionally rendered) */}
              {isLegendOpen && (
                <div style={{ padding: "12px 14px", paddingTop: "8px" }}>
                  <LegendSection title="Wave Height (m)" categories={WAVE_HEIGHT_CATEGORIES} />
                  <LegendSection title="Swell Wave Height (m)" categories={SWELL_HEIGHT_CATEGORIES} />
                  <LegendSection title="Wind Speed (km/hr)" categories={WIND_SPEED_CATEGORIES} />
                </div>
              )}
            </div>
          </div>

          <LayersControl position="topright">
            <LayersControl.BaseLayer name="Satellite" checked>
              <TileLayer
                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                attribution=' Map data &copy; Wind/Swell(Copernicus_WAVE), Wind Speed(WRF)'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topographic">
              <TileLayer
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                attribution=' Map data &copy; Wind/Swell(Copernicus_WAVE), Wind Speed(WRF)'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {boundaryData && (
            <GeoJSON
              data={boundaryData}
              style={{
                color: "black",
                weight: 4,
                opacity: 0.8,
              }}
            />
          )}

          {oceanData
            .reduce((uniqueLocations, data) => {
              if (!uniqueLocations.some((loc) => loc.id === data.location.id)) {
                uniqueLocations.push(data.location);
              }
              return uniqueLocations;
            }, [])
            .map((location) => {
              const locationName = location.name;
              const combinedData = groupedOceanData[locationName] || [];
              let maxWaveHeight = 0;

              const maxLength = combinedData.length;
              const chartData = [];
              const seenTimes = new Set();

              for (let i = 0; i < maxLength; i++) {
                const time =
                  combinedData[i]?.time ||
                  (i > 0
                    ? chartData[i - 1]?.time || "Invalid Time"
                    : "Invalid Time");
                const waveHeight = combinedData[i]?.waveHeight || 0;
                const swellWaveHeight = combinedData[i]?.swellWaveHeight || 0;
                const windSpeed = combinedData[i]?.windSpeed || 0;

                if (!seenTimes.has(time)) {
                  chartData.push({
                    time: time,
                    waveHeight: waveHeight,
                    swellWaveHeight: swellWaveHeight,
                    windSpeed: windSpeed,
                  });
                  seenTimes.add(time);
                }

                maxWaveHeight = Math.max(maxWaveHeight, waveHeight);
              }
              const category = getWaveCategory(maxWaveHeight);
              return (
                <CircleMarker
                  key={location.id}
                  center={[
                    parseFloat(location.latitude),
                    parseFloat(location.longitude),
                  ]}
                  color={category.color}
                  fillColor={category.color}
                  fillOpacity={1}
                  radius={8}
                  weight={2}
                  opacity={0.9}
                >
                  <Popup minWidth={500} maxWidth={520} maxHeight={500} className="ocean-popup">
                    <div style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", margin: "-13px -20px -13px -20px", overflow: "hidden", borderRadius: "12px" }}>
                      {/* Popup Header */}
                      <div style={{
                        // background: "linear-gradient(135deg, #1565c0, #1976d2)",
                        padding: "7px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}>
                        <FaCircleInfo size={18} style={{ color: "#1565c0", flexShrink: 0 }} />
                        <span style={{ color: "#1565c0", fontSize: "14px", fontWeight: "700", letterSpacing: "0.3px" }}>
                          {location.name}
                        </span>
                      </div>

                      {/* Info Cards */}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "10px",
                        padding: "14px 16px 10px 16px",
                      }}>
                        <div style={{
                          background: "#f5f7fa",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}>
                          <div style={{ fontSize: "10px", color: "#888", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "4px" }}>Municipality</div>
                          <div style={{ fontSize: "13px", color: "#333", fontWeight: "600" }}>{location.region || "N/A"}</div>
                        </div>
                        <div style={{
                          background: "#f5f7fa",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}>
                          <div style={{ fontSize: "10px", color: "#888", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "4px" }}>Max Wave Height</div>
                          <div style={{ fontSize: "16px", color: "#333", fontWeight: "700" }}>{maxWaveHeight.toFixed(2)} <span style={{ fontSize: "11px", fontWeight: "500" }}>m</span></div>
                        </div>
                        <div style={{
                          background: "#f5f7fa",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}>
                          <div style={{ fontSize: "10px", color: "#888", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "4px" }}>Category</div>
                          <div style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: category.color === "rgb(148, 203, 85)" ? "#4caf50" :
                                   category.color === "rgb(243, 235, 18)" ? "#f9a825" :
                                   category.color === "rgb(243, 155, 58)" ? "#e65100" : "#c62828",
                          }}>
                            {category.label.split("(")[0].trim()}
                          </div>
                        </div>
                      </div>

                      {/* Chart Section */}
                      {maxLength > 0 ? (
                        <div style={{
                          background: "#fafbfc",
                          borderRadius: "8px",
                          padding: "12px 8px 8px 8px",
                          border: "1px solid #eef0f2",
                          margin: "0 16px 14px 16px",
                        }}>
                          <div style={{ fontSize: "12px", fontWeight: "700", color: "#444", marginBottom: "8px", paddingLeft: "4px" }}>
                            3-Day Forecast — {locationName}
                          </div>
                          <div style={{ width: "480px", height: "280px" }}>
                            <WaveChart
                              data={chartData}
                              locationName={locationName}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          background: "#fff3e0",
                          borderRadius: "8px",
                          padding: "16px",
                          textAlign: "center",
                          color: "#e65100",
                          fontSize: "13px",
                          fontWeight: "500",
                          border: "1px solid #ffe0b2",
                          margin: "0 16px 14px 16px",
                        }}>
                          No forecast data available for this location.
                        </div>
                      )}
                    </div>
                  </Popup>
                  {showLabels && (
                    <Tooltip
                      permanent
                      direction="top"
                      offset={[0, -10]}
                      className="ocean-permanent-tooltip"
                    >
                      <div style={{ fontSize: "11px", lineHeight: "1.4", minWidth: "100px" }}>
                        <div style={{ fontWeight: "700", color: "#1565c0", marginBottom: "3px", borderBottom: "1px solid #e0e0e0", paddingBottom: "3px" }}>
                          {locationName}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: category.color,
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span style={{
                            fontWeight: "600",
                            color: category.color === "rgb(148, 203, 85)" ? "#4caf50" :
                                   category.color === "rgb(243, 235, 18)" ? "#f9a825" :
                                   category.color === "rgb(243, 155, 58)" ? "#e65100" : "#c62828",
                          }}>
                            {category.label.split("(")[0].trim()}
                          </span>
                        </div>
                        <div style={{ color: "#666", marginTop: "2px" }}>
                          <strong>Max:</strong> {maxWaveHeight.toFixed(2)} m
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </CircleMarker>
              );
            })}
          {/* <div className="source-note" style={{
              position: "absolute",
              bottom: "10px",
            }}>
            Source: Wind/Swell(Copernicus_WAVE), Wind Speed(WRF).
          </div> */}
        </MapContainer>
      </div>
    </div>
  );
};

export default Oceansate;